// "What kind of boat is this?" — the web half (ADR 0173 / core ADR-110).
//
// THE BOAT OWNS THE ANSWER. Every surface here relays the choice down and then
// waits for the boat's own echo to come back through the mirror; nothing is
// applied optimistically. That is not fussiness — the answer decides which mode
// rules the BOAT loads, so a screen that showed `Sail` while the boat's detector
// was still running the other set would be exactly the class of lie this whole
// program exists to remove. The states below are therefore the honest ones:
// `sending` while the relay is in flight, `sent` while the boat has the command
// but the mirror has not moved yet, `unconfirmed` once the boat has had long
// enough and said nothing, `offline` when it could not be delivered at all.
//
// The "in flight" claim is OWNED as request state and ended by the echo or by a
// deadline — never inferred from "the mirror disagrees with what I sent". That
// inference reproduces exactly when someone answers at the helm instead, and it
// would put "Sent to your boat…" on a page nobody has touched.
//
// Pure module: no React, no DOM. The components render its answers, and the
// unit tests run without a browser.

import type { VesselType } from '@neomaris/protocol';

import { api } from '../api';
import { isBoatOfflineRelayError } from './revealStaging';

export type { VesselType };

/** One offered answer: the wire value, the word, and the line under it. */
export interface VesselTypeOption {
  readonly value: VesselType;
  readonly label: string;
  readonly hint: string;
}

/**
 * The two answers, in offer order. Deliberately TWO — the old boat-side control
 * offered a third ("Catamaran") which answers a different question (how many
 * hulls) and left the one that matters unanswered, because catamarans come both
 * ways. The hints say what the answer is FOR, so it reads as a question with a
 * consequence rather than a profile field.
 */
export const VESSEL_TYPE_OPTIONS: readonly VesselTypeOption[] = [
  { value: 'power', label: 'Power', hint: 'engines only' },
  { value: 'sail', label: 'Sail', hint: 'sails, with or without an engine' },
];

/** The heading both the settings control and the prompt card ask under. */
export const VESSEL_TYPE_QUESTION = 'What kind of boat is this?';

/** Why we are asking, in one line — the prompt card's subtitle. */
export const VESSEL_TYPE_PROMPT_WHY =
  'This tunes which modes your alarms use — a power boat never needs the Sailing mode.';

/** The prompt card's own question, phrased as a question rather than a label. */
export const VESSEL_TYPE_PROMPT_QUESTION = 'Is this a power boat or a sailboat?';

/** The dismiss affordance on the prompt card. Not an answer — a "later". */
export const VESSEL_TYPE_DISMISS_LABEL = 'Not now';

/** The word for an answer, for the settings control's confirmation line. */
export function vesselTypeLabel(vesselType: VesselType | null | undefined): string | null {
  return VESSEL_TYPE_OPTIONS.find((o) => o.value === vesselType)?.label ?? null;
}

/**
 * Why the control is disabled while the boat is unreachable — VISIBLE text, not
 * a `title` tooltip.
 *
 * A tooltip answers this for a mouse and for nobody else. Most visits to these
 * pages are from a phone, where there is no hover and the reason simply does not
 * exist: the owner meets two dead buttons and no explanation. The offline case
 * is also not rare here — the question is asked of boats nobody has answered
 * for, which skews to boats that are not currently online.
 *
 * It names WHERE the setting lives, because that is the part that decides what
 * to do next: the boat keeps the answer, so nothing is lost by waiting, and
 * nothing can be fixed by tapping harder.
 */
export const VESSEL_TYPE_OFFLINE_REASON =
  'Your boat is offline — it keeps this answer, so this cannot set it from here. Answer at the boat, or from here once it is back online.';

// ---------------------------------------------------------------------------
// The relay call, and the honest states around it.
// ---------------------------------------------------------------------------

/**
 * What the control is doing right now.
 *
 * `sent` is the interesting one: the command reached the boat, and the mirror
 * has not caught up. It is NOT success-with-a-spinner — the boat may still
 * decline at its own trust boundary — so the copy says "Sent to your boat…"
 * rather than claiming the setting is saved.
 *
 * `sent` is an IN-FLIGHT REQUEST, owned by the page, and it is the page that
 * ends it: {@link settleVesselType} on the boat's echo, {@link timeOutVesselType}
 * when no echo arrives. It is deliberately NOT derived from "the mirror
 * disagrees with what I sent", which was the earlier shape and was wrong in a
 * way that misleads: an owner who answers at the helm — or a second browser, or
 * a `PUT /api/settings` from the boat's own sheet — moves the mirror
 * underneath a page nobody has touched, the derived mismatch appears, and that
 * page starts claiming a command is on its way to the boat. Nothing was sent.
 */
export type VesselTypeSaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending'; readonly vesselType: VesselType }
  | { readonly kind: 'sent'; readonly vesselType: VesselType }
  | { readonly kind: 'unconfirmed'; readonly vesselType: VesselType }
  | { readonly kind: 'offline' }
  | { readonly kind: 'error' };

/**
 * The boat's echo landed — end the in-flight request. Pure, so the page can run
 * it in an effect off the mirrored value and the tests can drive it without a
 * DOM.
 *
 * Returns the SAME object when nothing changes, so an effect calling
 * `setSave(settleVesselType(save, mirrored))` on every render cannot loop.
 */
export function settleVesselType(
  save: VesselTypeSaveState,
  mirrored: VesselType | null | undefined
): VesselTypeSaveState {
  if (save.kind !== 'sent' && save.kind !== 'unconfirmed') return save;
  return mirrored === save.vesselType ? IDLE : save;
}

/** The echo never came. Say so, rather than claiming the command is still moving. */
export function timeOutVesselType(save: VesselTypeSaveState): VesselTypeSaveState {
  return save.kind === 'sent' ? { kind: 'unconfirmed', vesselType: save.vesselType } : save;
}

const IDLE: VesselTypeSaveState = { kind: 'idle' };

/**
 * Copy for each non-idle state. `null` when there is nothing left to say —
 * which is what happens the moment the boat's echo lands, because from then on
 * the control's own selected button is the answer.
 *
 * Note what is NOT here any more: a "Your boat has this: Sail." line derived
 * from comparing the mirror to what was sent. The selected state already says
 * it, and deriving a sentence from that comparison is what let an out-of-band
 * change resurrect status text on an untouched page.
 */
export function vesselTypeStatusText(state: VesselTypeSaveState): string | null {
  switch (state.kind) {
    case 'sending':
      return 'Sending to your boat…';
    case 'sent':
      return 'Sent to your boat — this page updates when your boat confirms it.';
    case 'unconfirmed':
      // Not an error and not a success. The relay accepted it, so the answer is
      // not lost — but the boat has not said it took it, and until it does the
      // mode rules aboard are still the old ones.
      return 'Your boat has not confirmed that yet. It takes effect when your boat picks it up, and this page updates then.';
    case 'offline':
      return 'Your boat is offline. It keeps this answer, so bring it online and try again.';
    case 'error':
      return 'Couldn’t send that to your boat — try again.';
    case 'idle':
      return null;
  }
}

/**
 * Relay one answer to the boat. Resolves to the state the caller should show;
 * it never throws, because every failure here has a sentence above.
 */
export async function sendVesselType(
  boatId: string,
  vesselType: VesselType
): Promise<VesselTypeSaveState> {
  try {
    await api(`/v1/boats/${boatId}/vessel-type`, {
      method: 'POST',
      body: { vesselType },
      idempotencyKey: crypto.randomUUID(),
    });
    return { kind: 'sent', vesselType };
  } catch (err) {
    return isBoatOfflineRelayError(err) ? { kind: 'offline' } : { kind: 'error' };
  }
}

// ---------------------------------------------------------------------------
// The prompt card's "Not now", remembered per user + per boat.
// ---------------------------------------------------------------------------

/**
 * Where a dismissal is remembered. Per BOAT, because an owner with a power boat
 * and a sailboat answers the two differently and dismissing one says nothing
 * about the other. `localStorage` rather than a server column: this is a
 * preference about a nag, not about the boat, and it should not cost a
 * migration or survive onto someone else's screen.
 */
export function vesselTypePromptDismissKey(boatId: string): string {
  return `nm:vessel-type-prompt-dismissed:${boatId}`;
}

/** Whether the prompt has been dismissed on THIS browser for this boat. */
export function isVesselTypePromptDismissed(boatId: string): boolean {
  try {
    return window.localStorage.getItem(vesselTypePromptDismissKey(boatId)) === '1';
  } catch {
    // Private mode / storage disabled. Asking again is the safe failure: the
    // question is one tap and the alternative is never asking at all.
    return false;
  }
}

/** Remember a "Not now". Storage failures are silent — see above. */
export function dismissVesselTypePrompt(boatId: string): void {
  try {
    window.localStorage.setItem(vesselTypePromptDismissKey(boatId), '1');
  } catch {
    /* nothing to do — the card simply asks again next visit */
  }
}

/**
 * Should the Alarms page raise the prompt card?
 *
 * Three conditions, all required:
 *
 *   * the answer is genuinely UNKNOWN — never re-ask a boat that has answered;
 *   * the boat is ONLINE — the only way to answer is a relay to the boat, and a
 *     card whose every button 409s is worse than no card;
 *   * this browser has not been told "Not now" for this boat.
 */
export function shouldPromptForVesselType(opts: {
  readonly vesselType: VesselType | null | undefined;
  readonly boatOnline: boolean;
  readonly dismissed: boolean;
}): boolean {
  if (opts.vesselType === 'power' || opts.vesselType === 'sail') return false;
  return opts.boatOnline && !opts.dismissed;
}
